import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtNumber, fmtDate, parseNum, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, Receipt, CreditCard } from "@phosphor-icons/react";
import { useSort, SortTh } from "@/components/sortable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

function StatCard({ label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "border-[#E2E8F0] bg-white",
    danger: "border-[#FECACA] bg-[#FEF2F2]",
    warn: "border-[#FED7AA] bg-[#FFF7ED]",
  };
  return (
    <div data-testid={testid} className={`rounded-md border px-5 py-4 ${tones[tone]}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">{label}</p>
      <p className="text-2xl font-black tracking-tight text-[#0F172A] font-heading mt-1">{value}</p>
      {sub && <p className="text-xs text-[#64748B] mt-0.5">{sub}</p>}
    </div>
  );
}

function VencBadge({ dias, vencida }) {
  if (dias == null) return <span className="text-xs text-[#94A3B8]">Sin fecha</span>;
  if (vencida) return <span className="text-xs font-bold px-2 py-0.5 rounded bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]">Vencida hace {Math.abs(dias)}d</span>;
  if (dias <= 7) return <span className="text-xs font-bold px-2 py-0.5 rounded bg-[#FFF7ED] text-[#9A3412] border border-[#FED7AA]">Vence en {dias}d</span>;
  return <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">En {dias}d</span>;
}

const PAGO_LABEL = { pagado: "Pagado", abono: "Abono", pendiente: "Pendiente" };

export default function CuentasPorPagar() {
  const [data, setData] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payMonto, setPayMonto] = useState("");
  const [paySaving, setPaySaving] = useState(false);
  const [payAbonos, setPayAbonos] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/cuentas-por-pagar").then((r) => setData(r.data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openPay = (c, e) => {
    e.stopPropagation();
    setPayTarget(c);
    setPayMonto("");
    setPayAbonos(null);
    api.get(`/lotes/${c.lote_id}/abonos`).then((r) => setPayAbonos(r.data)).catch(() => setPayAbonos([]));
  };

  const aplicarPago = async (monto) => {
    setPaySaving(true);
    try {
      await api.post(`/lotes/${payTarget.lote_id}/abonos`, { monto });
      toast.success("Abono registrado");
      setPayTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo registrar el pago");
    } finally {
      setPaySaving(false);
    }
  };

  const registrarAbono = async () => {
    const monto = parseNum(payMonto);
    if (isNaN(monto) || monto <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    if (monto > (payTarget.saldo || 0) + 0.001) {
      toast.error("El abono no puede superar el saldo pendiente");
      return;
    }
    await aplicarPago(monto);
  };

  const pagarTotal = async () => {
    await aplicarPago(payTarget.saldo || 0);
  };

  const { sorted, sortKey, sortDir, toggleSort } = useSort(data?.cuentas || []);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32">
        <CircleNotch size={32} className="animate-spin text-[#0F172A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-up" data-testid="cuentas-por-pagar-page">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-md bg-[#FEF2F2] flex items-center justify-center">
          <Receipt size={22} weight="bold" className="text-[#991B1B]" />
        </span>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Cuentas por pagar</h1>
          <p className="text-[#475569] mt-1">Lotes con saldo pendiente, ordenados por fecha de vencimiento de la factura.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard testid="cxp-total" label="Total por pagar" value={fmtMoney(data.total_saldo)} sub={`${fmtNumber(data.num_cuentas)} factura(s)`} />
        <StatCard testid="cxp-vencido" label="Vencido" value={fmtMoney(data.total_vencido)} sub={`${fmtNumber(data.num_vencidas)} vencida(s)`} tone="danger" />
        <StatCard testid="cxp-proximas" label="Próximas a vencer (≤7 días)" value={fmtNumber(data.num_proximas)} sub="facturas por vencer" tone="warn" />
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
        {data.cuentas.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Receipt size={40} weight="duotone" className="mx-auto text-[#94A3B8] mb-3" />
            <p className="text-[#64748B]">No hay cuentas por pagar. ¡Todo al día!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cxp-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                  <SortTh label="Proveedor" sortKey="proveedor_nombre" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Lote / Producto" sortKey="numero_lote" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Nº factura" sortKey="numero_factura" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Vencimiento" sortKey="factura_vencimiento" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Estado" sortKey="estado_pago" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Bruto" sortKey="factura_bruto" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Abonado" sortKey="monto_abonado" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Saldo" sortKey="saldo" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => (
                  <tr
                    key={c.lote_id}
                    data-testid={`cxp-row-${c.lote_id}`}
                    onClick={() => navigate(`/lotes/${c.lote_id}`)}
                    className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-3 font-semibold text-[#0F172A]">{c.proveedor_nombre || "—"}</td>
                    <td className="px-5 py-3 text-[#475569]">
                      <span className="font-medium text-[#0F172A]">{c.numero_lote}</span>
                      <span className="block text-xs text-[#94A3B8]">{c.producto_nombre}</span>
                    </td>
                    <td className="px-5 py-3 text-[#475569]">{c.numero_factura || "—"}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[#475569]">{c.factura_vencimiento ? fmtDate(c.factura_vencimiento) : "—"}</span>
                        <VencBadge dias={c.dias_hasta_vencimiento} vencida={c.vencida} />
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded border ${c.estado_pago === "abono" ? "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]" : "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]"}`}>
                        {PAGO_LABEL[c.estado_pago] || "Pendiente"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-[#475569]">{fmtMoney(c.factura_bruto, c.moneda)}</td>
                    <td className="px-5 py-3 text-right text-[#475569]">{fmtMoney(c.monto_abonado, c.moneda)}</td>
                    <td className="px-5 py-3 text-right font-bold text-[#0F172A]">{fmtMoney(c.saldo, c.moneda)}</td>
                    <td className="px-5 py-3 text-right">
                      <Button
                        size="sm"
                        data-testid={`pagar-btn-${c.lote_id}`}
                        onClick={(e) => openPay(c, e)}
                        className="h-8 bg-[#0F172A] hover:bg-[#1E293B] text-white"
                      >
                        <CreditCard size={15} weight="bold" className="mr-1" /> Pagar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={Boolean(payTarget)} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent data-testid="pago-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">Registrar pago</DialogTitle>
            <DialogDescription className="text-[#64748B]">
              {payTarget && (
                <>Factura {payTarget.numero_factura || "—"} · {payTarget.proveedor_nombre || "Sin proveedor"} · Lote {payTarget.numero_lote}</>
              )}
            </DialogDescription>
          </DialogHeader>
          {payTarget && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border border-[#E2E8F0] bg-[#F8F9FA] px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">Bruto</p>
                  <p className="text-sm font-bold text-[#0F172A]">{fmtMoney(payTarget.factura_bruto, payTarget.moneda)}</p>
                </div>
                <div className="rounded-md border border-[#E2E8F0] bg-[#F8F9FA] px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">Abonado</p>
                  <p className="text-sm font-bold text-[#0F172A]">{fmtMoney(payTarget.monto_abonado, payTarget.moneda)}</p>
                </div>
                <div className="rounded-md border border-[#FECACA] bg-[#FEF2F2] px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#991B1B]">Saldo</p>
                  <p className="text-sm font-bold text-[#991B1B]" data-testid="pago-saldo">{fmtMoney(payTarget.saldo, payTarget.moneda)}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Monto a abonar</Label>
                <Input
                  data-testid="pago-monto-input"
                  value={payMonto}
                  onChange={(e) => setPayMonto(e.target.value)}
                  placeholder="0,00"
                  className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
                />
                <p className="text-xs text-[#94A3B8]">Si el abono cubre el saldo, la factura quedará marcada como pagada.</p>
              </div>
              <div className="border-t border-[#E2E8F0] pt-3">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B] mb-2">Historial de abonos</p>
                {payAbonos === null ? (
                  <p className="text-xs text-[#94A3B8]">Cargando…</p>
                ) : payAbonos.length === 0 ? (
                  <p className="text-xs text-[#94A3B8]" data-testid="abonos-empty">Aún no hay abonos registrados.</p>
                ) : (
                  <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="abonos-list">
                    {payAbonos.map((a) => (
                      <li key={a.id} className="flex items-center justify-between text-sm border-b border-[#F1F5F9] pb-1">
                        <span className="text-[#64748B]">{fmtDate(a.fecha)}</span>
                        <span className="font-semibold text-[#0F172A]">{fmtMoney(a.monto, payTarget.moneda)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={pagarTotal} disabled={paySaving} data-testid="pago-total-btn" className="border-[#E2E8F0]">
              Pagar saldo total
            </Button>
            <Button onClick={registrarAbono} disabled={paySaving} data-testid="pago-abono-btn" className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
              {paySaving ? <CircleNotch size={18} className="animate-spin" /> : "Registrar abono"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
